import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';

const ACTION_LABELS = {
  connexion: 'Connexion', import: 'Import', enrichissement: 'Enrichissement',
  modification_prospect: 'Modification', affectation: 'Affectation',
  resultat_appel: 'Résultat appel', note: 'Note', rappel: 'Rappel',
  export: 'Export', creation_utilisateur: 'Création user', modification_utilisateur: 'Modif. user',
  desactivation_utilisateur: 'Désactivation', reset_password: 'Reset MDP',
  suppression_import: 'Suppression', modification_parametres: 'Paramètres'
};

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');

  const loadLogs = useCallback(async () => {
    const params = { page, per_page: 50 };
    if (filter) params.action = filter;
    try {
      const { data } = await api.get('/logs', { params });
      setLogs(data.logs);
      setTotal(data.total);
    } catch {}
  }, [page, filter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Logs d'activité</h1>
          <p className="text-sm text-[#71717A] mt-1">{total} entrées</p>
        </div>
        <Select value={filter} onValueChange={v => { setFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48 h-9 bg-white" data-testid="log-filter"><SelectValue placeholder="Filtrer par action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                {['Date', 'Utilisateur', 'Action', 'Cible', 'Détail'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-[#71717A] font-medium text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="py-12 text-center text-[#71717A]">Aucun log</td></tr>
              ) : logs.map(log => (
                <tr key={log.id} className="border-b border-[#E5E7EB]/50 hover:bg-[#F9FAFB]/50">
                  <td className="py-3 px-4 text-xs text-[#71717A] whitespace-nowrap">
                    {new Date(log.date_action).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="py-3 px-4 font-medium text-xs">{log.utilisateur_nom || '-'}</td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary" className="text-xs">{ACTION_LABELS[log.action] || log.action}</Badge>
                  </td>
                  <td className="py-3 px-4 text-xs text-[#71717A]">{log.cible_type}</td>
                  <td className="py-3 px-4 text-xs text-[#71717A] max-w-[300px] truncate">{log.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#71717A]">Page {page} / {totalPages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 w-8 p-0">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-8 w-8 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
